import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Clock,
  User,
  Package,
  Truck,
  MessageCircle,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Avatar, AvatarFallback } from "@shared/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  TICKET_STATUS_COLORS,
  TICKET_CATEGORY_COLORS,
} from "@shared/utils/constants";
import { useUpdateTicketMutation } from "@features/tickets/hooks/useTickets";

const TicketCard = ({ ticket, isManagerOrAdmin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateTicketMutation = useUpdateTicketMutation();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const handleTicketClick = () => {
    navigate(`/tickets/${ticket._id}`);
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === ticket.status) return;

    setIsUpdatingStatus(true);
    try {
      await updateTicketMutation.mutateAsync({
        id: ticket._id,
        updateData: { status: newStatus },
      });
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    return (
      TICKET_STATUS_COLORS[status] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const getCategoryColor = (category) => {
    return (
      TICKET_CATEGORY_COLORS[category] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "pickup_issue":
      case "delivery_issue":
        return <Truck className="h-4 w-4" />;
      case "package_damage":
        return <Package className="h-4 w-4" />;
      default:
        return <MessageCircle className="h-4 w-4" />;
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return date;
    }
  };

  const getInitials = (name, email) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3
                className="font-medium text-lg text-gray-900 hover:text-blue-600 transition-colors"
                onClick={handleTicketClick}
              >
                {ticket.title}
              </h3>
              {ticket.isUrgent && (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              )}
            </div>

            <div className="flex items-center gap-2 mb-2">
              {/* Status Badge with Dropdown for Admin/Manager */}
              {isManagerOrAdmin ? (
                <Select
                  value={ticket.status}
                  onValueChange={handleStatusChange}
                  disabled={isUpdatingStatus}
                >
                  <SelectTrigger
                    className={`h-7 w-auto border-0 ${getStatusColor(
                      ticket.status
                    )}`}
                  >
                    <SelectValue>
                      {t(`tickets.status.${ticket.status}`, ticket.status)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">
                      {t("tickets.status.open", "Open")}
                    </SelectItem>
                    <SelectItem value="in_progress">
                      {t("tickets.status.in_progress", "In Progress")}
                    </SelectItem>
                    <SelectItem value="waiting_for_response">
                      {t(
                        "tickets.status.waiting_for_response",
                        "Waiting for Response"
                      )}
                    </SelectItem>
                    <SelectItem value="resolved">
                      {t("tickets.status.resolved", "Resolved")}
                    </SelectItem>
                    <SelectItem value="closed">
                      {t("tickets.status.closed", "Closed")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge
                  variant="outline"
                  className={getStatusColor(ticket.status)}
                >
                  {t(`tickets.status.${ticket.status}`, ticket.status)}
                </Badge>
              )}

              <Badge
                variant="outline"
                className={getCategoryColor(ticket.category)}
              >
                <div className="flex items-center gap-1">
                  {getCategoryIcon(ticket.category)}
                  <span>
                    {t(`tickets.category.${ticket.category}`, ticket.category)}
                  </span>
                </div>
              </Badge>
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm font-medium text-gray-900">
              #{ticket.ticketNumber}
            </div>
            <div className="text-xs text-gray-500">
              {formatDate(ticket.createdAt)}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {ticket.description}
        </p>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {/* Creator Info */}
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs">
                  {getInitials(ticket.creator?.fullName, ticket.creator?.email)}
                </AvatarFallback>
              </Avatar>
              <span>{ticket.creator?.fullName || ticket.creator?.email}</span>
              <Badge variant="secondary" className="text-xs">
                {t(`role.${ticket.creator?.role}`, ticket.creator?.role)}
              </Badge>
            </div>

            {/* Assignment Info for Admin/Manager */}
            {isManagerOrAdmin && ticket.assignedTo && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>
                  {t("tickets.assignedTo", "Assigned to")}:{" "}
                  {ticket.assignedTo.fullName || ticket.assignedTo.email}
                </span>
              </div>
            )}

            {/* Message Count */}
            <div className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              <span>{ticket.messageCount || ticket.messages?.length || 0}</span>
            </div>

            {/* Last Response Time */}
            {ticket.lastResponseAt && (
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>
                  {t("tickets.lastResponse", "Last response")}:{" "}
                  {formatDate(ticket.lastResponseAt)}
                </span>
              </div>
            )}
          </div>

          <Button variant="outline" size="sm" onClick={handleTicketClick}>
            {t("tickets.viewDetails", "View Details")}
          </Button>
        </div>

        {/* Related Package/Pickup Info */}
        {(ticket.relatedPackage || ticket.relatedPickup) && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {ticket.relatedPackage && (
                <div className="flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  <span>
                    {t("tickets.relatedPackage", "Package")}:
                    {ticket.relatedPackage.trackingNumber ||
                      ticket.relatedPackage._id}
                  </span>
                </div>
              )}

              {ticket.relatedPickup && (
                <div className="flex items-center gap-1">
                  <Truck className="h-4 w-4" />
                  <span>
                    {t("tickets.relatedPickup", "Pickup")}:
                    {ticket.relatedPickup.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TicketCard;
